import React, { useLayoutEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
export default function PanelSheet({
  title,
  close,
  children,
  footer,
  wide = false,
  compact = false,
  closeLabel = 'Paneli kapat',
  busy = false
}: {
  title: string;
  close: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
  compact?: boolean;
  closeLabel?: string;
  busy?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const state = useRef({ busy, close });
  state.current = { busy, close };
  useLayoutEffect(() => {
    const dialog = ref.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Native listeners guard the browser close request before React unmounts a dialog.
    const cancel = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!state.current.busy) state.current.close();
    };
    const keydown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const dialogs = document.querySelectorAll('dialog[open]');
      if (dialogs[dialogs.length - 1] !== dialog) return;
      // Handle only the top modal before Chromium cascades native close requests.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!state.current.busy) state.current.close();
    };
    dialog?.addEventListener('cancel', cancel);
    document.addEventListener('keydown', keydown, true);
    dialog?.showModal();
    return () => {
      dialog?.removeEventListener('cancel', cancel);
      document.removeEventListener('keydown', keydown, true);
      dialog?.close();
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  return createPortal(<dialog
    ref={ref}
    className={`user-sheet ${wide ? 'user-sheet-wide' : ''} ${compact ? 'user-sheet-confirmation' : ''}`}
    onClick={event => {
    if (!busy && event.target === event.currentTarget) close();
  }}
    aria-labelledby={titleId}
  >
    <div className="flex h-full flex-col bg-white">
      <header className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
        <h2 id={titleId} className="font-semibold text-lg">
          {title}
        </h2>
        <button aria-label={closeLabel} disabled={busy} onClick={close} className="rounded-xl bg-gray-100 p-3">
          <X size={18} />
        </button>
      </header>
      <div className="overflow-y-auto flex-1 min-h-0 p-5">
        {children}
      </div>
      {footer && <footer className="border-t border-gray-100 p-5 shrink-0">
        {footer}
      </footer>}
    </div>
  </dialog>, document.body);
}
